import { useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { axiosInstance } from "../../../../utils/networking";
import { fmt } from "../useReportFilter";

/**
 * `GET /reports/ecosystem` contract (thrive_server/models/metricsModel.ts →
 * getEcosystemReport). Every non-deleted referral in the range, grouped by the
 * partner it was sent to (provider first, resource otherwise).
 */
export interface EcosystemPartner {
  id: string;
  name: string;
  kind: "provider" | "resource";
  category: string;
  byStatus: { [status: string]: number };
  total: number;
  distinctClients: number;
  completionRate: number;
}

export interface EcosystemTotals {
  referrals: number;
  completed: number;
  completionRate: number;
  partners: number;
}

export interface EcosystemResponse {
  range: { start: string | null; end: string | null };
  totals: EcosystemTotals;
  partners: EcosystemPartner[];
  categories: { category: string; total: number }[];
}

export type PartnerKindFilter = "all" | "provider" | "resource";

/**
 * Completion rate as the server defines it: completed ÷ referrals that
 * progressed past "referred". Recomputed client-side so the stat row stays
 * honest when the kind filter narrows the partner set.
 */
export const completionRateOf = (byStatus: {
  [status: string]: number;
}): number => {
  const progressed = Object.keys(byStatus)
    .filter((s) => s !== "referred")
    .reduce((sum, s) => sum + (byStatus[s] || 0), 0);
  if (progressed === 0) return 0;
  return (byStatus["completed"] || 0) / progressed;
};

export const COMPLETION_RATE_CAPTION =
  "completed ÷ referrals past “referred”";

// Module-level cache mirroring useMetrics: tab switches must not re-hit an
// expensive aggregation for a filter key that is already on screen.
const cache = new Map<string, EcosystemResponse>();
const inflight = new Map<string, Promise<EcosystemResponse>>();

export const ecosystemCacheKey = (
  start: Dayjs | null,
  end: Dayjs | null,
  consultant?: string,
) => `${fmt(start)}|${fmt(end)}|${consultant || ""}`;

export const clearEcosystemCache = () => {
  cache.clear();
  inflight.clear();
};

export const fetchEcosystem = (
  start: Dayjs | null,
  end: Dayjs | null,
  consultant?: string,
): Promise<EcosystemResponse> => {
  const key = ecosystemCacheKey(start, end, consultant);
  const cached = cache.get(key);
  if (cached) return Promise.resolve(cached);
  const pending = inflight.get(key);
  if (pending) return pending;

  const params: { [k: string]: string } = {};
  if (start) params.startDate = fmt(start);
  if (end) params.endDate = fmt(end);
  if (consultant) params.consultant = consultant;

  const promise = axiosInstance
    .get<EcosystemResponse>("/reports/ecosystem", { params })
    .then((res) => {
      cache.set(key, res.data);
      return res.data;
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, promise);
  return promise;
};

export const describeEcosystemError = (err: any): string => {
  const status = err?.response?.status;
  if (status === 403) return "You are not authorized to view this report.";
  if (status === 404)
    return "The ecosystem endpoint is not available (GET /reports/ecosystem).";
  return (
    err?.response?.data?.message ||
    err?.message ||
    "Failed to load the referrals report."
  );
};

export interface UseEcosystemResult {
  data: EcosystemResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export const useEcosystem = (
  start: Dayjs | null,
  end: Dayjs | null,
  consultant?: string,
): UseEcosystemResult => {
  // The consultant is part of the key: an admin switching consultants must
  // never be served another consultant's cached numbers. Both referral tabs
  // still share one entry per (range, consultant).
  const key = ecosystemCacheKey(start, end, consultant);
  const [data, setData] = useState<EcosystemResponse | null>(
    () => cache.get(key) || null,
  );
  const [loading, setLoading] = useState(!cache.has(key));
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const cached = cache.get(key);
    if (cached && nonce === 0) {
      setData(cached);
      setLoading(false);
      setError(null);
      return;
    }
    // A new range or consultant means the numbers on screen are for the wrong
    // scope.
    setData(null);
    setLoading(true);
    setError(null);
    fetchEcosystem(start, end, consultant)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setData(null);
        setError(describeEcosystemError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  return {
    data,
    loading,
    error,
    refresh: () => {
      cache.delete(key);
      setNonce((n) => n + 1);
    },
  };
};

export default useEcosystem;
